import { useState } from "react";
import { Link } from "react-router";
import { Plus, X } from "lucide-react";
import { api } from "@/lib/api";
import { useQuery, invalidate } from "@/lib/useQuery";
import type { Todo, AccountListItem } from "@/lib/types";
import { dueLabel, todayIso } from "@/lib/format";
import { Button, PageHeader, Loading, Empty, Checkbox, AiTag, Tag, Modal, Field, toast, Card } from "@/components/ui";
import { useMe } from "@/app/MeContext";

export function TodosPage() {
  const me = useMe();
  const [scope, setScope] = useState<"mine" | "all">("mine");
  const [showDone, setShowDone] = useState(false);
  const [creating, setCreating] = useState(false);
  const key = `todos:${scope}:${showDone}`;
  const { data, loading, refetch } = useQuery<Todo[]>(key, () => api.get<Todo[]>(`/todos?scope=${scope}&done=${showDone ? "all" : "0"}`), [scope, showDone]);
  const open = (data ?? []).filter((t) => !t.done);
  const overdue = open.filter((t) => t.dueDate && t.dueDate < todayIso()).length;
  return (
    <>
      <PageHeader title="Todos" sub={data ? `${open.length} open${overdue ? ` · ${overdue} overdue` : ""}` : undefined}>
        {me.role === "admin" && <div className="seg"><button className={scope === "mine" ? "active" : ""} onClick={() => setScope("mine")}>Mine</button><button className={scope === "all" ? "active" : ""} onClick={() => setScope("all")}>Everyone</button></div>}
        <label className="row gap-6 small muted-2"><input type="checkbox" checked={showDone} onChange={(e) => setShowDone(e.target.checked)} /> Show done</label>
        <Button variant="primary" onClick={() => setCreating(true)}><Plus size={14} /> New todo</Button>
      </PageHeader>
      <div className="page">
        <Card>
          {loading && !data ? <Loading /> : (data ?? []).length === 0 ? <Empty>Nothing to do. Inbound mail with action items will add todos here.</Empty> : (data ?? []).map((t) => <TodoRow key={t.id} todo={t} onChange={() => { refetch(); invalidate("badges"); }} showAssignee={scope === "all"} />)}
        </Card>
      </div>
      {creating && <NewTodoModal onClose={() => setCreating(false)} onCreated={() => { setCreating(false); refetch(); invalidate("badges"); }} />}
    </>
  );
}

export function TodoRow({ todo: t, onChange, showAssignee, compact }: { todo: Todo; onChange: () => void; showAssignee?: boolean; compact?: boolean }) {
  const due = dueLabel(t.dueDate);
  const toggle = async (done: boolean) => { await api.patch(`/todos/${t.id}`, { done }); onChange(); };
  const dismiss = async () => { await api.patch(`/todos/${t.id}`, { dismissed: true }); toast("Dismissed"); onChange(); };
  return (
    <div className={`todo-row ${t.done ? "done" : ""}`}>
      <Checkbox checked={t.done} onChange={toggle} label={t.title} />
      <div className="title">
        <div className="truncate">{t.title}</div>
        <div className={`small muted ${compact ? "" : "mobile-only"}`}>{[t.accountName, t.done ? "Done" : due.text].filter(Boolean).map((x, i) => <span key={i} className={i > 0 && !t.done ? due.cls === "overdue" || due.cls === "now" ? "" : "" : ""} style={i > 0 && !t.done && due.cls ? { color: "var(--status-red-text)" } : undefined}>{i > 0 ? " · " : ""}{x}</span>)}</div>
      </div>
      {t.aiGenerated && <AiTag />}
      {!compact && t.accountName && (t.accountId ? <Link to={`/accounts/${t.accountId}`} className="pill desktop-only">{t.accountName}</Link> : t.partnerId ? <Link to={`/partners/${t.partnerId}`} className="pill desktop-only">{t.accountName} · Partner</Link> : <Tag color="outline">{t.accountName}</Tag>)}
      {!compact && showAssignee && t.assigneeName && <span className="small muted desktop-only">{t.assigneeName}</span>}
      {!compact && (t.done ? <span className="due desktop-only">Done</span> : <span className={`due desktop-only ${due.cls}`}>{due.text}</span>)}
      {t.aiGenerated && !t.done && <button className="btn ghost sm icon" title="Dismiss (tells Claude this was wrong)" onClick={dismiss}><X size={13} /></button>}
    </div>
  );
}

export function NewTodoModal({ onClose, onCreated, accountId, partnerId, partnerName }: { onClose: () => void; onCreated: () => void; accountId?: string; partnerId?: string; partnerName?: string }) {
  const [f, setF] = useState({ title: "", dueDate: "", accountId: accountId ?? "" });
  const { data: accounts } = useQuery<AccountListItem[]>("accounts", () => api.get<AccountListItem[]>("/accounts"));
  const save = async () => {
    try { await api.post("/todos", { title: f.title, dueDate: f.dueDate || null, accountId: partnerId ? null : f.accountId || null, partnerId: partnerId ?? null }); onCreated(); } catch (e) { toast((e as Error).message, true); }
  };
  return (
    <Modal title="New todo" onClose={onClose} footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" disabled={!f.title.trim()} onClick={save}>Add</Button></>}>
      <Field label="What"><input className="input" autoFocus value={f.title} onChange={(e) => setF((x) => ({ ...x, title: e.target.value }))} onKeyDown={(e) => e.key === "Enter" && f.title.trim() && save()} /></Field>
      <div className="form-grid">
        <Field label="Due"><input className="input" type="date" value={f.dueDate} onChange={(e) => setF((x) => ({ ...x, dueDate: e.target.value }))} /></Field>
        {partnerId ? <Field label="Partner"><input className="input" value={partnerName ?? "Partner"} readOnly /></Field> : <Field label="Account"><select className="select" value={f.accountId} onChange={(e) => setF((x) => ({ ...x, accountId: e.target.value }))}><option value="">None</option>{(accounts ?? []).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</select></Field>}
      </div>
    </Modal>
  );
}
